import React, { useState, useEffect, useCallback } from 'react';
import { expenseAPI, inventoryAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const INCOME_CATEGORIES = [
  {v:'collection', l:'💰 Collection Received'},
  {v:'product_sale', l:'📦 Product Sale (ONT/STB/Other)'},
  {v:'cash_in_hand', l:'💵 Cash in Hand (Opening Balance)'},
  {v:'bank_in', l:'🏦 Bank Receipt'},
  {v:'other_income', l:'📈 Other Income'},
];

const EXPENSE_CATEGORIES = [
  {v:'product_purchase', l:'📦 Product Purchase (ONT/STB/Other)'},
  {v:'bank_deposit', l:'🏦 Cash Deposit to Bank'},
  {v:'chitty', l:'📋 Chitty Payment'},
  {v:'loan', l:'🏦 Loan / EMI'},
  {v:'staff_advance', l:'👨‍💼 Staff Advance'},
  {v:'salary', l:'💵 Staff Salary'},
  {v:'rent', l:'🏠 Rent'},
  {v:'electricity', l:'⚡ Electricity'},
  {v:'travel', l:'🚗 Travel'},
  {v:'maintenance', l:'🔧 Maintenance'},
  {v:'bank_out', l:'🏦 Bank Transfer Out'},
  {v:'other', l:'📦 Other Expense'},
];

const CATEGORIES = [...INCOME_CATEGORIES.map(c=>({...c,type:'income'})), ...EXPENSE_CATEGORIES.map(c=>({...c,type:'expense'}))];

const MODES = ['Cash','UPI','Bank Transfer','Cheque'];

const today = () => new Date().toISOString().split('T')[0];


const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const getYears = () => {
  const current = new Date().getFullYear();
  const years = [];
  for(let y=2020; y<=current+2; y++) years.push(y);
  return years;
};

const monthYearRange = (month, year) => {
  const last = new Date(year, month, 0).getDate();
  const m = String(month).padStart(2,'0');
  return {
    from: year+'-'+m+'-01',
    to: year+'-'+m+'-'+String(last).padStart(2,'0')
  };
};


export default function LCOExpenses() {
  const { tenant } = useAuth();
  const primary = tenant?.branding?.primary_color || '#1B6B3A';

  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [search, setSearch] = useState('');
  const [selMonth, setSelMonth] = useState(String(new Date().getMonth()+1));
  const [selYear, setSelYear] = useState(String(new Date().getFullYear()));
  const [lastLoaded, setLastLoaded] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterCat, setFilterCat] = useState('');
  const _initRange = monthYearRange(new Date().getMonth()+1, new Date().getFullYear());
  const [fromDate, setFromDate] = useState(_initRange.from);
  const [toDate, setToDate] = useState(_initRange.to);
  const fromDateRef = React.useRef(_initRange.from);
  const toDateRef = React.useRef(_initRange.to);
  const [form, setForm] = useState({date:today(), category:'other', type:'expense', amount:'', mode:'Cash', source:'', note:''});
  const [sources, setSources] = useState([]);
  const [products, setProducts] = useState([]);
  const [activeTab, setActiveTab] = useState('expenses');
  const [stockData, setStockData] = useState([]);
  const [stockLoading, setStockLoading] = useState(false);
  const [pnlData, setPnlData] = useState(null);
  const [pnlLoading, setPnlLoading] = useState(false);
  const [pnlYear, setPnlYear] = useState(String(new Date().getFullYear()));
  const [expandedPnlMonth, setExpandedPnlMonth] = useState(null);
  const [invProduct, setInvProduct] = useState('');
  const [invNewProduct, setInvNewProduct] = useState('');
  const [invVendor, setInvVendor] = useState('');
  const [invModel, setInvModel] = useState('');
  const [invQty, setInvQty] = useState('1');
  const [invUnitPrice, setInvUnitPrice] = useState('');
  const [invCustomer, setInvCustomer] = useState('');
  const [invPurchasedFrom, setInvPurchasedFrom] = useState('');
  const [collectionSubType, setCollectionSubType] = useState('broadband');
  const [bankName, setBankName] = useState('');

  const load = async (overrideFrom, overrideTo) => {
    setLoading(true);
    setExpenses([]);
    setSummary(null);
    try {
      const params = {};
      const fd = overrideFrom !== undefined ? overrideFrom : fromDate;
      const td = overrideTo !== undefined ? overrideTo : toDate;
      if(fd) params.from_date = fd;
      if(td) params.to_date = td;
      if(filterCat) params.category = filterCat;
      if(filterType !== 'all') params.type = filterType;
      const [r, sr, pr] = await Promise.all([expenseAPI.getAll(params), expenseAPI.getSources(), inventoryAPI.getProducts()]);
      setSources(sr.data.sources||[]);
      setProducts(pr.data.data||[]);
      setExpenses(r.data.data||[]);
      setSummary(r.data.summary||null);
    } catch(e){ console.error(e); }
    setLoading(false);
  };

  useEffect(()=>{ load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const isProductCategory = ['product_purchase','product_sale'].includes(form.category);

  const loadPnl = async (year) => {
    setPnlLoading(true);
    try {
      const r = await expenseAPI.getPnl(year||pnlYear);
      setPnlData(r.data);
    } catch(e){console.error(e);}
    setPnlLoading(false);
  };

  const loadStock = async () => {
    setStockLoading(true);
    try { const r = await inventoryAPI.getStock(); setStockData(r.data.data||[]); }
    catch(e){console.error(e);}
    setStockLoading(false);
  };

  // Pre-fill product when ont/stb selected
  React.useEffect(()=>{
    if(collectionSubType==='ont_sale') setInvProduct('ONT');
    else if(collectionSubType==='stb_sale') setInvProduct('STB');
  },[collectionSubType]);

  const handleInvQtyPrice = (qty, price) => {
    setInvQty(qty); setInvUnitPrice(price);
    setForm(f=>({...f, amount:((parseFloat(qty)||0)*(parseFloat(price)||0)).toFixed(2)}));
  };

  const handleCategoryChange = (cat) => {
    const found = CATEGORIES.find(c=>c.v===cat);
    setForm(f=>({...f, category:cat, type:found?.type||'expense'}));
    if(cat==='collection') setCollectionSubType('broadband');
    if(cat!=='collection') setCollectionSubType('');
  };

  const submitWithInventory = async () => {
    if(!form.amount||!form.date) return alert('Date and amount required');
    if(!invProduct&&!invNewProduct) return alert('Select or enter a product');
    if(!invQty||!invUnitPrice) return alert('Quantity and unit price required');
    let productName = invProduct==='__new__'||!invProduct ? invNewProduct : invProduct;
    if(invNewProduct) { await inventoryAPI.addProduct({name:invNewProduct,type:form.category==='product_purchase'?'purchase':'sale'}); setInvNewProduct(''); }
    const invResult = await inventoryAPI.addMovement({product:productName,vendor:invVendor,model:invModel,quantity:parseInt(invQty),unit_price:parseFloat(invUnitPrice),amount:parseFloat(form.amount),movement_type:form.category==='product_purchase'?'purchase':'sale',date:form.date,note:form.note,customer_username:invCustomer,purchased_from:invPurchasedFrom});
    if(invResult.data.warning) alert('⚠️ '+invResult.data.warning);
    await submit();
  };

  const submit = async () => {
    if(!form.amount || !form.date) return alert('Date and amount required');
    if(form.category==='bank_deposit' && !bankName) return alert('Please enter bank name');
    const submitForm = form.category==='bank_deposit' ? {...form, source:bankName, note:(form.note?form.note+' | ':'')+'Bank: '+bankName} : form;
    try {
      if(editEntry) {
        await expenseAPI.update(editEntry._id, submitForm);
      } else {
        await expenseAPI.add(submitForm);
      }
      setShowForm(false);
      setEditEntry(null);
      setForm({date:today(), category:'other', type:'expense', amount:'', mode:'Cash', note:''});
      load();
    } catch(e){ alert('Error: '+e.message); }
  };

  const handleEdit = (e) => {
    setEditEntry(e);
    setForm({
      date: e.date ? new Date(e.date).toISOString().split('T')[0] : today(),
      category: e.category, type: e.type,
      amount: e.amount, mode: e.mode, source: e.source||'', note: e.note
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if(!window.confirm('Delete this entry?')) return;
    await expenseAPI.remove(id);
    load();
  };

  const filtered = expenses.filter(e => {
    if(!search) return true;
    return e.note?.toLowerCase().includes(search.toLowerCase()) ||
           e.category?.toLowerCase().includes(search.toLowerCase());
  });

  const exportExcel = () => {
    const rows = [['Date','Category','Type','Amount','Mode','Source','Note']];
    filtered.forEach(e => rows.push([
      new Date(e.date).toLocaleDateString('en-IN'),
      CATEGORIES.find(c=>c.v===e.category)?.l||e.category,
      e.type.toUpperCase(), e.amount, e.mode,
      e.source||'', e.note||''
    ]));
    rows.push(['','','','','','']);
    rows.push(['','','TOTAL INCOME',filtered.filter(e=>e.type==='income').reduce((a,e)=>a+e.amount,0),'','']);
    rows.push(['','','TOTAL EXPENSE',filtered.filter(e=>e.type==='expense').reduce((a,e)=>a+e.amount,0),'','']);
    rows.push(['','','NET BALANCE',(filtered.reduce((a,e)=>e.type==='income'?a+e.amount:a-e.amount,0)),'','']);
    const csv = rows.map(r=>r.join(',')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv],{type:'text/csv'}));
    a.download = 'expenses_'+fromDate+'_to_'+toDate+'.csv';
    a.click();
  };

  const exportPdf = () => {
    const totInc = filtered.filter(e=>e.type==='income').reduce((a,e)=>a+e.amount,0);
    const totExp = filtered.filter(e=>e.type==='expense').reduce((a,e)=>a+e.amount,0);
    const netBal = totInc - totExp;
    const rows = filtered.map((e,i) => `
      <tr style="background:${i%2?'#f9fafb':'white'}">
        <td>${new Date(e.date).toLocaleDateString('en-IN')}</td>
        <td>${CATEGORIES.find(c=>c.v===e.category)?.l||e.category}</td>
        <td><span style="color:${e.type==='income'?'green':'red'};font-weight:700">${e.type.toUpperCase()}</span></td>
        <td style="text-align:right;font-weight:700;color:${e.type==='income'?'green':'red'}">${e.type==='income'?'+':'-'}₹${e.amount?.toLocaleString('en-IN')}</td>
        <td>${e.mode}</td>
        <td style="color:#64748b;font-size:11px">${e.note||'-'}</td>
        <td style="text-align:right;font-weight:700;color:${(e.cash_balance||0)>=0?'#16a34a':'#dc2626'}">₹${(e.cash_balance||0).toLocaleString('en-IN')}</td>
        <td style="text-align:right;font-weight:700;color:${(e.bank_balance||0)>=0?'#2563eb':'#dc2626'}">₹${(e.bank_balance||0).toLocaleString('en-IN')}</td>
      </tr>`).join('');
    const totalRow = `
      <tr style="background:#1B6B3A;color:white;font-weight:bold">
        <td colspan="2">TOTALS (${filtered.length} entries)</td>
        <td style="color:#86efac">▲ INCOME</td>
        <td style="text-align:right;color:#86efac">₹${totInc.toLocaleString('en-IN')}</td>
        <td colspan="2"></td>
      </tr>
      <tr style="background:#dc2626;color:white;font-weight:bold">
        <td colspan="2"></td>
        <td style="color:#fca5a5">▼ EXPENSE</td>
        <td style="text-align:right;color:#fca5a5">₹${totExp.toLocaleString('en-IN')}</td>
        <td colspan="2"></td>
      </tr>
      <tr style="background:#1e40af;color:white;font-weight:bold">
        <td colspan="2"></td>
        <td>NET BALANCE</td>
        <td style="text-align:right">₹${netBal.toLocaleString('en-IN')}</td>
        <td colspan="2"></td>
      </tr>`;
    const w = window.open('','_blank');
    w.document.write(`<!DOCTYPE html><html><head><title>Expense Report</title>
    <style>body{font-family:Arial;padding:20px}table{width:100%;border-collapse:collapse;font-size:12px}
    th{background:#1B6B3A;color:white;padding:8px;text-align:left}td{padding:6px 8px;border-bottom:1px solid #eee}
    h2{color:#1B6B3A}.summary{display:flex;gap:16px;margin:16px 0;flex-wrap:wrap}
    .scard{background:#f0fdf4;border:1px solid #bbf7d0;border-radius:8px;padding:12px;text-align:center;min-width:120px}
    .scard .val{font-size:18px;font-weight:800}.scard .lbl{font-size:11px;color:#64748b}
    .total-row{background:#1B6B3A;color:white;font-weight:bold}
    @media print{body{print-color-adjust:exact}}</style>
    </head><body>
    <h2>📊 Expense Report</h2>
    <p>Period: ${fromDate} to ${toDate} | Generated: ${new Date().toLocaleString('en-IN')}</p>
    <div class="summary">
      <div class="scard"><div class="val" style="color:green">₹${(summary?.total_income||0).toLocaleString('en-IN')}</div><div class="lbl">Total Income</div></div>
      <div class="scard"><div class="val" style="color:red">₹${(summary?.total_expense||0).toLocaleString('en-IN')}</div><div class="lbl">Total Expense</div></div>
      <div class="scard"><div class="val" style="color:${(summary?.balance||0)>=0?'green':'red'}">₹${(summary?.balance||0).toLocaleString('en-IN')}</div><div class="lbl">Balance</div></div>
      <div class="scard"><div class="val">${filtered.length}</div><div class="lbl">Entries</div></div>
    </div>
    <table><thead><tr><th>Date</th><th>Category</th><th>Type</th><th>Amount</th><th>Mode</th><th>Note</th><th style='text-align:right;color:#16a34a'>💵 Cash</th><th style='text-align:right;color:#2563eb'>🏦 Bank</th></tr></thead>
    <tbody>${rows}${totalRow}
    </tbody></table></body></html>`);
    w.document.close(); w.print();
  };

  return (
    <div style={{padding:'16px', maxWidth:'1200px', margin:'0 auto'}}>
      {/* Tabs */}
      <div style={{display:'flex',gap:'8px',marginBottom:'16px'}}>
        {[['expenses','📒 Expenses'],['pnl','📊 P&L Report'],['inventory','📦 Inventory']].map(([t,l])=>(
          <button key={t} onClick={()=>{setActiveTab(t);if(t==='inventory')loadStock();if(t==='pnl')loadPnl();}}
            style={{padding:'8px 20px',border:'none',borderRadius:'8px',cursor:'pointer',fontSize:'13px',fontWeight:'700',
              background:activeTab===t?primary:'#f1f5f9',color:activeTab===t?'white':'#64748b'}}>
            {l}
          </button>
        ))}
      </div>

      {activeTab==='pnl' && (
        <div>
          {/* Year selector */}
          <div style={{display:'flex',gap:'8px',marginBottom:'16px',alignItems:'center',flexWrap:'wrap'}}>
            <span style={{fontSize:'12px',fontWeight:'600',color:'#64748b'}}>Year:</span>
            {[...Array(3)].map((_,i)=>{
              const y = String(new Date().getFullYear()-i);
              return (
                <button key={y} onClick={()=>{setPnlYear(y);loadPnl(y);}}
                  style={{padding:'6px 16px',border:'2px solid',borderRadius:'8px',cursor:'pointer',fontSize:'12px',fontWeight:'700',
                    borderColor:pnlYear===y?primary:'#e2e8f0',
                    background:pnlYear===y?primary:'white',
                    color:pnlYear===y?'white':'#64748b'}}>
                  {y}
                </button>
              );
            })}
          </div>

          {pnlLoading ? <div style={{textAlign:'center',padding:'40px',color:'#94a3b8'}}>Loading...</div> : pnlData && (
            <div>
              {/* Annual Summary */}
              <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'12px',marginBottom:'16px'}}>
                <div style={{background:'#f0fdf4',borderRadius:'10px',padding:'14px',textAlign:'center',border:'1px solid #bbf7d0'}}>
                  <div style={{fontSize:'11px',fontWeight:'700',color:'#16a34a',marginBottom:'4px'}}>TOTAL INCOME {pnlYear}</div>
                  <div style={{fontSize:'20px',fontWeight:'900',color:'#16a34a'}}>₹{(pnlData.annual?.income||0).toLocaleString('en-IN')}</div>
                </div>
                <div style={{background:'#fef2f2',borderRadius:'10px',padding:'14px',textAlign:'center',border:'1px solid #fecaca'}}>
                  <div style={{fontSize:'11px',fontWeight:'700',color:'#dc2626',marginBottom:'4px'}}>TOTAL EXPENSE {pnlYear}</div>
                  <div style={{fontSize:'20px',fontWeight:'900',color:'#dc2626'}}>₹{(pnlData.annual?.expense||0).toLocaleString('en-IN')}</div>
                </div>
                <div style={{background:(pnlData.annual?.profit||0)>=0?'#eff6ff':'#fef2f2',borderRadius:'10px',padding:'14px',textAlign:'center',border:'1px solid #bfdbfe'}}>
                  <div style={{fontSize:'11px',fontWeight:'700',color:'#2563eb',marginBottom:'4px'}}>NET PROFIT {pnlYear}</div>
                  <div style={{fontSize:'20px',fontWeight:'900',color:(pnlData.annual?.profit||0)>=0?'#2563eb':'#dc2626'}}>
                    {(pnlData.annual?.profit||0)>=0?'+':''}₹{(pnlData.annual?.profit||0).toLocaleString('en-IN')}
                  </div>
                </div>
              </div>

              {/* Monthly breakdown */}
              <div style={{background:'white',borderRadius:'12px',border:'1px solid #f1f5f9',overflow:'hidden'}}>
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:'12px'}}>
                  <thead><tr style={{background:'#f8fafc'}}>
                    <th style={{padding:'10px 12px',textAlign:'left',fontWeight:'700',color:'#64748b'}}>Month</th>
                    <th style={{padding:'10px 12px',textAlign:'right',fontWeight:'700',color:'#16a34a'}}>Income</th>
                    <th style={{padding:'10px 12px',textAlign:'right',fontWeight:'700',color:'#dc2626'}}>Expense</th>
                    <th style={{padding:'10px 12px',textAlign:'right',fontWeight:'700',color:'#2563eb'}}>Profit/Loss</th>
                    <th style={{padding:'10px 12px',textAlign:'right',fontWeight:'700',color:'#16a34a'}}>Cash Income</th>
                    <th style={{padding:'10px 12px',textAlign:'right',fontWeight:'700',color:'#2563eb'}}>Bank Income</th>
                    <th style={{padding:'10px 12px',textAlign:'center',fontWeight:'700',color:'#64748b'}}>Entries</th>
                  </tr></thead>
                  <tbody>
                    {pnlData.months?.map((m,i)=>(
                      <tr key={i} onClick={()=>setExpandedPnlMonth(expandedPnlMonth===m.month?null:m.month)}
                        style={{borderTop:'1px solid #f1f5f9',background:expandedPnlMonth===m.month?'#eff6ff':i%2?'#fafafa':'white',cursor:'pointer'}}>
                        <td style={{padding:'10px 12px',fontWeight:'700',color:'#0f172a'}}>{m.label} {m.year}</td>
                        <td style={{padding:'10px 12px',textAlign:'right',color:'#16a34a',fontWeight:'600'}}>
                          {m.income>0?'₹'+m.income.toLocaleString('en-IN'):'-'}
                        </td>
                        <td style={{padding:'10px 12px',textAlign:'right',color:'#dc2626',fontWeight:'600'}}>
                          {m.expense>0?'₹'+m.expense.toLocaleString('en-IN'):'-'}
                        </td>
                        <td style={{padding:'10px 12px',textAlign:'right',fontWeight:'800',
                          color:m.profit>=0?'#2563eb':'#dc2626'}}>
                          {m.count>0?(m.profit>=0?'+':'')+('₹'+Math.abs(m.profit).toLocaleString('en-IN')):'-'}
                        </td>
                        <td style={{padding:'10px 12px',textAlign:'right',color:'#64748b',fontSize:'11px'}}>
                          {m.cash_income>0?'₹'+m.cash_income.toLocaleString('en-IN'):'-'}
                        </td>
                        <td style={{padding:'10px 12px',textAlign:'right',color:'#64748b',fontSize:'11px'}}>
                          {m.bank_income>0?'₹'+m.bank_income.toLocaleString('en-IN'):'-'}
                        </td>
                        <td style={{padding:'10px 12px',textAlign:'center',color:'#94a3b8'}}>{m.count||'-'}</td>
                      </tr>
                    ))}
                    {/* Annual total row */}
                    <tr style={{borderTop:'2px solid #e2e8f0',background:'#1B6B3A'}}>
                      <td style={{padding:'10px 12px',fontWeight:'800',color:'white'}}>TOTAL {pnlYear}</td>
                      <td style={{padding:'10px 12px',textAlign:'right',fontWeight:'800',color:'#86efac'}}>₹{(pnlData.annual?.income||0).toLocaleString('en-IN')}</td>
                      <td style={{padding:'10px 12px',textAlign:'right',fontWeight:'800',color:'#fca5a5'}}>₹{(pnlData.annual?.expense||0).toLocaleString('en-IN')}</td>
                      <td style={{padding:'10px 12px',textAlign:'right',fontWeight:'800',color:(pnlData.annual?.profit||0)>=0?'#bfdbfe':'#fca5a5'}}>
                        {(pnlData.annual?.profit||0)>=0?'+':''}₹{(pnlData.annual?.profit||0).toLocaleString('en-IN')}
                      </td>
                      <td colSpan={3}></td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Print P&L button */}
              <div style={{marginTop:'12px',textAlign:'right'}}>
                <button onClick={()=>{
                  const rows = pnlData.months?.filter(m=>m.count>0).map(m=>`
                    <tr><td>${m.label} ${m.year}</td>
                    <td style="text-align:right;color:green">₹${m.income.toLocaleString('en-IN')}</td>
                    <td style="text-align:right;color:red">₹${m.expense.toLocaleString('en-IN')}</td>
                    <td style="text-align:right;color:${m.profit>=0?'blue':'red'};font-weight:bold">${m.profit>=0?'+':''}₹${m.profit.toLocaleString('en-IN')}</td>
                    <td style="text-align:center">${m.count}</td></tr>`).join('');
                  const w=window.open('','_blank');
                  w.document.write(`<!DOCTYPE html><html><head><title>P&L Report ${pnlYear}</title>
                  <style>body{font-family:Arial;padding:20px}table{width:100%;border-collapse:collapse;font-size:13px}
                  th{background:#1B6B3A;color:white;padding:10px;text-align:left}td{padding:8px 10px;border-bottom:1px solid #eee}
                  h2{color:#1B6B3A}.summary{display:flex;gap:20px;margin:16px 0}
                  .scard{border-radius:8px;padding:16px;text-align:center;min-width:140px}
                  .total-row{background:#1B6B3A;color:white;font-weight:bold}
                  @media print{body{print-color-adjust:exact}}</style></head><body>
                  <h2>📊 Profit & Loss Report — ${pnlYear}</h2>
                  <p>Generated: ${new Date().toLocaleString('en-IN')}</p>
                  <div class="summary">
                    <div class="scard" style="background:#f0fdf4;border:1px solid #bbf7d0">
                      <div style="font-size:11px;color:#16a34a;font-weight:700">TOTAL INCOME</div>
                      <div style="font-size:22px;font-weight:900;color:#16a34a">₹${(pnlData.annual?.income||0).toLocaleString('en-IN')}</div>
                    </div>
                    <div class="scard" style="background:#fef2f2;border:1px solid #fecaca">
                      <div style="font-size:11px;color:#dc2626;font-weight:700">TOTAL EXPENSE</div>
                      <div style="font-size:22px;font-weight:900;color:#dc2626">₹${(pnlData.annual?.expense||0).toLocaleString('en-IN')}</div>
                    </div>
                    <div class="scard" style="background:#eff6ff;border:1px solid #bfdbfe">
                      <div style="font-size:11px;color:#2563eb;font-weight:700">NET PROFIT</div>
                      <div style="font-size:22px;font-weight:900;color:${(pnlData.annual?.profit||0)>=0?'#2563eb':'#dc2626'}">${(pnlData.annual?.profit||0)>=0?'+':''}₹${(pnlData.annual?.profit||0).toLocaleString('en-IN')}</div>
                    </div>
                  </div>
                  <table><thead><tr><th>Month</th><th>Income</th><th>Expense</th><th>Profit/Loss</th><th>Entries</th></tr></thead>
                  <tbody>${rows}
                  <tr class="total-row"><td>TOTAL ${pnlYear}</td>
                  <td style="text-align:right">₹${(pnlData.annual?.income||0).toLocaleString('en-IN')}</td>
                  <td style="text-align:right">₹${(pnlData.annual?.expense||0).toLocaleString('en-IN')}</td>
                  <td style="text-align:right">${(pnlData.annual?.profit||0)>=0?'+':''}₹${(pnlData.annual?.profit||0).toLocaleString('en-IN')}</td>
                  <td></td></tr>
                  </tbody></table></body></html>`);
                  w.document.close();w.print();
                }} style={{padding:'8px 20px',background:'#dc2626',color:'white',border:'none',borderRadius:'8px',cursor:'pointer',fontSize:'13px',fontWeight:'700'}}>
                  🖨️ Print P&L Report
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab==='inventory' && (
        <div style={{background:'white',borderRadius:'12px',border:'1px solid #f1f5f9',overflow:'hidden'}}>
          {stockLoading ? <div style={{textAlign:'center',padding:'40px',color:'#94a3b8'}}>Loading...</div> : (
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:'12px'}}>
              <thead><tr style={{background:'#f8fafc'}}>
                <th style={{padding:'10px 12px',textAlign:'left',fontWeight:'700',color:'#64748b'}}>Product</th>
                <th style={{padding:'10px 12px',textAlign:'left',fontWeight:'700',color:'#64748b'}}>Vendor</th>
                <th style={{padding:'10px 12px',textAlign:'left',fontWeight:'700',color:'#64748b'}}>Purchased From</th>
                <th style={{padding:'10px 12px',textAlign:'left',fontWeight:'700',color:'#64748b'}}>Model</th>
                <th style={{padding:'10px 12px',textAlign:'right',fontWeight:'700',color:'#64748b'}}>Purchased</th>
                <th style={{padding:'10px 12px',textAlign:'right',fontWeight:'700',color:'#64748b'}}>Sold</th>
                <th style={{padding:'10px 12px',textAlign:'right',fontWeight:'700',color:'#64748b'}}>In Hand</th>
                <th style={{padding:'10px 12px',textAlign:'right',fontWeight:'700',color:'#64748b'}}>Profit</th>
              </tr></thead>
              <tbody>
                {stockData.length===0 ? (
                  <tr><td colSpan={7} style={{padding:'40px',textAlign:'center',color:'#94a3b8'}}>No inventory yet. Add a product purchase to get started.</td></tr>
                ) : stockData.map((s,i)=>(
                  <tr key={i} style={{borderTop:'1px solid #f1f5f9',background:i%2?'#fafafa':'white'}}>
                    <td style={{padding:'8px 12px',fontWeight:'700',color:'#2563eb'}}>{s.product}</td>
                    <td style={{padding:'8px 12px',color:'#64748b'}}>{s.vendor||'-'}</td>
                    <td style={{padding:'8px 12px',color:'#64748b'}}>{s.purchased_from||'-'}</td>
                    <td style={{padding:'8px 12px',color:'#64748b'}}>{s.model||'-'}</td>
                    <td style={{padding:'8px 12px',textAlign:'right'}}>{s.purchased}</td>
                    <td style={{padding:'8px 12px',textAlign:'right'}}>{s.sold}</td>
                    <td style={{padding:'8px 12px',textAlign:'right',fontWeight:'800',color:s.in_hand>0?'#16a34a':'#dc2626'}}>{s.in_hand}</td>
                    <td style={{padding:'8px 12px',textAlign:'right',fontWeight:'700',color:s.profit>=0?'#16a34a':'#dc2626'}}>₹{s.profit?.toLocaleString('en-IN')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {activeTab==='expenses' && (<>
      {/* Header */}
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'16px',flexWrap:'wrap',gap:'8px'}}>
        <div>
          <h2 style={{margin:0,fontSize:'20px',fontWeight:'800',color:'#0f172a'}}>📒 Daily Expense Tracker</h2>
          <p style={{margin:'2px 0 0',fontSize:'12px',color:'#64748b'}}>Personal bookkeeping — visible only to you</p>
          {lastLoaded && <p style={{margin:'2px 0 0',fontSize:'12px',color:'#2563eb',fontWeight:'600'}}>📊 Showing: {lastLoaded}</p>}
        </div>
        <button onClick={()=>{setEditEntry(null);setForm({date:today(),category:'other',type:'expense',amount:'',mode:'Cash',note:''});setShowForm(true);}}
          style={{padding:'10px 18px',background:primary,color:'white',border:'none',borderRadius:'8px',cursor:'pointer',fontSize:'13px',fontWeight:'700'}}>
          ➕ Add Entry
        </button>
      </div>

      {/* Cash Book Balance Cards */}
      {expenses.length>0 && (
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px',marginBottom:'12px'}}>
          <div style={{background:'#f0fdf4',borderRadius:'10px',padding:'14px',border:'2px solid #16a34a',textAlign:'center'}}>
            <div style={{fontSize:'11px',fontWeight:'700',color:'#16a34a',marginBottom:'4px'}}>💵 CASH IN HAND</div>
            <div style={{fontSize:'22px',fontWeight:'900',color:expenses[0]?.cash_balance>=0?'#16a34a':'#dc2626'}}>
              ₹{(expenses[0]?.cash_balance||0).toLocaleString('en-IN')}
            </div>
          </div>
          <div style={{background:'#eff6ff',borderRadius:'10px',padding:'14px',border:'2px solid #2563eb',textAlign:'center'}}>
            <div style={{fontSize:'11px',fontWeight:'700',color:'#2563eb',marginBottom:'4px'}}>🏦 BANK BALANCE</div>
            <div style={{fontSize:'22px',fontWeight:'900',color:expenses[0]?.bank_balance>=0?'#2563eb':'#dc2626'}}>
              ₹{(expenses[0]?.bank_balance||0).toLocaleString('en-IN')}
            </div>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      {summary && (
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))',gap:'12px',marginBottom:'16px'}}>
          {[
            {l:'Total Income',v:'₹'+(summary.total_income||0).toLocaleString('en-IN'),c:'#16a34a',bg:'#f0fdf4'},
            {l:'Total Expense',v:'₹'+(summary.total_expense||0).toLocaleString('en-IN'),c:'#dc2626',bg:'#fef2f2'},
            {l:'Balance',v:'₹'+(summary.balance||0).toLocaleString('en-IN'),c:(summary.balance||0)>=0?'#16a34a':'#dc2626',bg:(summary.balance||0)>=0?'#f0fdf4':'#fef2f2'},
            {l:'Entries',v:summary.count||0,c:'#2563eb',bg:'#eff6ff'},
          ].map(s=>(
            <div key={s.l} style={{background:s.bg,borderRadius:'10px',padding:'14px',textAlign:'center',border:'1px solid #f1f5f9'}}>
              <div style={{fontSize:'18px',fontWeight:'800',color:s.c}}>{s.v}</div>
              <div style={{fontSize:'11px',color:'#64748b',marginTop:'2px'}}>{s.l}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div style={{background:'white',borderRadius:'12px',padding:'12px',marginBottom:'12px',border:'1px solid #f1f5f9',display:'flex',gap:'8px',flexWrap:'wrap',alignItems:'center'}}>
        {/* Month + Year filter */}
        <select value={selMonth} onChange={e=>{
          setSelMonth(e.target.value);
          if(e.target.value&&selYear){
            const {from,to}=monthYearRange(parseInt(e.target.value),parseInt(selYear));
            setFromDate(from);setToDate(to);
            fromDateRef.current=from;toDateRef.current=to;
            load(from,to);
          } else{setFromDate('');setToDate('');}
        }} style={{padding:'6px 10px',border:'1px solid #e2e8f0',borderRadius:'8px',fontSize:'12px'}}>
          <option value="">📅 Month</option>
          {MONTHS.map((m,i)=><option key={i} value={i+1}>{m}</option>)}
        </select>
        <select value={selYear} onChange={e=>{
          setSelYear(e.target.value);
          if(selMonth&&e.target.value){
            const {from,to}=monthYearRange(parseInt(selMonth),parseInt(e.target.value));
            setFromDate(from);setToDate(to);
            fromDateRef.current=from;toDateRef.current=to;
            load(from,to);
          } else if(e.target.value){
            const from=e.target.value+'-01-01';const to=e.target.value+'-12-31';
            setFromDate(from);setToDate(to);
            fromDateRef.current=from;toDateRef.current=to;
            load(from,to);
          } else{setFromDate('');setToDate('');}
        }} style={{padding:'6px 10px',border:'1px solid #e2e8f0',borderRadius:'8px',fontSize:'12px'}}>
          <option value="">Year</option>
          {getYears().map(y=><option key={y} value={y}>{y}</option>)}
        </select>
        <button onClick={()=>load(fromDateRef.current,toDateRef.current)}
          style={{padding:'6px 14px',background:'#1B6B3A',color:'white',border:'none',borderRadius:'8px',fontSize:'12px',fontWeight:'700',cursor:'pointer'}}>
          🔍 Generate
        </button>
        <button onClick={()=>{setSelMonth('');setSelYear('');setFromDate('');setToDate('');}}
          style={{padding:'6px 10px',border:'1px solid #e2e8f0',borderRadius:'8px',fontSize:'12px',cursor:'pointer',background:'white',color:'#64748b'}}>
          ✕ Clear
        </button>
        <input type="date" value={fromDate} onChange={e=>{setFromDate(e.target.value);fromDateRef.current=e.target.value;setSelMonth('');setSelYear('');}}
          style={{padding:'6px 10px',border:'1px solid #e2e8f0',borderRadius:'8px',fontSize:'12px'}}/>
        <span style={{color:'#64748b',fontSize:'12px'}}>to</span>
        <input type="date" value={toDate} onChange={e=>{setToDate(e.target.value);toDateRef.current=e.target.value;setSelMonth('');setSelYear('');}}
          style={{padding:'6px 10px',border:'1px solid #e2e8f0',borderRadius:'8px',fontSize:'12px'}}/>
        <select value={filterType} onChange={e=>setFilterType(e.target.value)}
          style={{padding:'6px 10px',border:'1px solid #e2e8f0',borderRadius:'8px',fontSize:'12px'}}>
          <option value="all">All Types</option>
          <option value="income">Income</option>
          <option value="expense">Expense</option>
        </select>
        <select value={filterCat} onChange={e=>setFilterCat(e.target.value)}
          style={{padding:'6px 10px',border:'1px solid #e2e8f0',borderRadius:'8px',fontSize:'12px'}}>
          <option value="">All Categories</option>
          {CATEGORIES.map(c=><option key={c.v} value={c.v}>{c.l}</option>)}
        </select>
        <input type="text" value={search} onChange={e=>setSearch(e.target.value)}
          placeholder="🔍 Search note..."
          style={{padding:'6px 10px',border:'1px solid #e2e8f0',borderRadius:'8px',fontSize:'12px',flex:1,minWidth:'150px'}}/>
        <button onClick={exportExcel} style={{padding:'6px 12px',background:'#16a34a',color:'white',border:'none',borderRadius:'8px',cursor:'pointer',fontSize:'12px',fontWeight:'600'}}>📥 Excel</button>
        <button onClick={exportPdf} style={{padding:'6px 12px',background:'#dc2626',color:'white',border:'none',borderRadius:'8px',cursor:'pointer',fontSize:'12px',fontWeight:'600'}}>🖨️ PDF</button>
      </div>

      {/* Entries Table */}
      {loading ? <div style={{textAlign:'center',padding:'40px',color:'#94a3b8'}}>Loading...</div> : (
        <div style={{background:'white',borderRadius:'12px',border:'1px solid #f1f5f9',overflow:'hidden'}}>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:'12px'}}>
            <thead><tr style={{background:'#f8fafc'}}>
              <th style={{padding:'10px 12px',textAlign:'left',fontWeight:'700',color:'#64748b'}}>Date</th>
              <th style={{padding:'10px 12px',textAlign:'left',fontWeight:'700',color:'#64748b'}}>Category</th>
              <th style={{padding:'10px 12px',textAlign:'left',fontWeight:'700',color:'#64748b'}}>Type</th>
              <th style={{padding:'10px 12px',textAlign:'right',fontWeight:'700',color:'#64748b'}}>Amount</th>
              <th style={{padding:'10px 12px',textAlign:'left',fontWeight:'700',color:'#64748b'}}>Mode</th>
              <th style={{padding:'10px 12px',textAlign:'left',fontWeight:'700',color:'#64748b'}}>Source</th>
              <th style={{padding:'10px 12px',textAlign:'left',fontWeight:'700',color:'#64748b'}}>Note</th>
              <th style={{padding:'10px 12px',textAlign:'right',fontWeight:'700',color:'#16a34a'}}>💵 Cash</th>
              <th style={{padding:'10px 12px',textAlign:'right',fontWeight:'700',color:'#2563eb'}}>🏦 Bank</th>
              <th style={{padding:'10px 12px',textAlign:'center',fontWeight:'700',color:'#64748b'}}>Actions</th>
            </tr></thead>
            <tbody>
              {filtered.length===0 ? (
                <tr><td colSpan={7} style={{padding:'40px',textAlign:'center',color:'#94a3b8'}}>No entries found</td></tr>
              ) : filtered.map((e,i)=>(
                <tr key={i} style={{borderTop:'1px solid #f1f5f9',background:i%2?'#fafafa':'white'}}>
                  <td style={{padding:'8px 12px',fontWeight:'600'}}>{new Date(e.date).toLocaleDateString('en-IN')}</td>
                  <td style={{padding:'8px 12px'}}>{CATEGORIES.find(c=>c.v===e.category)?.l||e.category}</td>
                  <td style={{padding:'8px 12px'}}>
                    <span style={{padding:'2px 8px',borderRadius:'12px',fontSize:'10px',fontWeight:'700',
                      background:e.type==='income'?'#f0fdf4':'#fef2f2',
                      color:e.type==='income'?'#16a34a':'#dc2626'}}>
                      {e.type==='income'?'▲ INCOME':'▼ EXPENSE'}
                    </span>
                  </td>
                  <td style={{padding:'8px 12px',textAlign:'right',fontWeight:'800',fontSize:'13px',
                    color:e.type==='income'?'#16a34a':'#dc2626'}}>
                    {e.type==='income'?'+':'-'}₹{e.amount?.toLocaleString('en-IN')}
                  </td>
                  <td style={{padding:'8px 12px',color:'#64748b'}}>{e.mode}</td>
                  <td style={{padding:'8px 12px',color:'#2563eb',fontWeight:'600'}}>{e.source||'-'}</td>
                  <td style={{padding:'8px 12px',color:'#64748b'}}>{e.note||'-'}</td>
                  <td style={{padding:'8px 12px',textAlign:'right',fontWeight:'700',fontSize:'11px',color:e.cash_balance>=0?'#16a34a':'#dc2626'}}>
                    ₹{(e.cash_balance||0).toLocaleString('en-IN')}
                  </td>
                  <td style={{padding:'8px 12px',textAlign:'right',fontWeight:'700',fontSize:'11px',color:e.bank_balance>=0?'#2563eb':'#dc2626'}}>
                    ₹{(e.bank_balance||0).toLocaleString('en-IN')}
                  </td>
                  <td style={{padding:'8px 12px',textAlign:'center',display:'flex',gap:'4px',justifyContent:'center'}}>
                    <button onClick={()=>handleEdit(e)}
                      style={{padding:'4px 8px',background:'#f1f5f9',border:'none',borderRadius:'6px',cursor:'pointer',fontSize:'11px'}}>✏️</button>
                    <button onClick={()=>handleDelete(e._id)}
                      style={{padding:'4px 8px',background:'#fef2f2',border:'none',borderRadius:'6px',cursor:'pointer',fontSize:'11px'}}>🗑️</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      </>
      )}

      {/* Add/Edit Modal */}
      {showForm && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}}>
          <div style={{background:'white',borderRadius:'16px',padding:'24px',width:'380px',maxWidth:'90vw',maxHeight:'90vh',overflowY:'auto'}}>
            <h3 style={{margin:'0 0 16px',fontSize:'16px',fontWeight:'800'}}>{editEntry?'✏️ Edit Entry':'➕ Add Entry'}</h3>
            <div style={{display:'grid',gap:'12px'}}>
              <div>
                <label style={{fontSize:'12px',fontWeight:'600',color:'#374151',display:'block',marginBottom:'4px'}}>Date</label>
                <input type="date" value={form.date} onChange={e=>setForm(f=>({...f,date:e.target.value}))}
                  style={{width:'100%',padding:'8px 12px',border:'1px solid #e2e8f0',borderRadius:'8px',fontSize:'13px',boxSizing:'border-box'}}/>
              </div>
              <div>
                <label style={{fontSize:'12px',fontWeight:'600',color:'#374151',display:'block',marginBottom:'4px'}}>Category</label>
                <select value={form.category} onChange={e=>handleCategoryChange(e.target.value)}
                  style={{width:'100%',padding:'8px 12px',border:'1px solid #e2e8f0',borderRadius:'8px',fontSize:'13px'}}>
                  {form.type==='income' ? (
                    INCOME_CATEGORIES.map(c=><option key={c.v} value={c.v}>{c.l}</option>)
                  ) : (
                    EXPENSE_CATEGORIES.map(c=><option key={c.v} value={c.v}>{c.l}</option>)
                  )}
                </select>
              </div>
              <div>
                <label style={{fontSize:'12px',fontWeight:'600',color:'#374151',display:'block',marginBottom:'4px'}}>Type</label>
                <div style={{display:'flex',gap:'8px'}}>
                  {['income','expense'].map(t=>(
                    <button key={t} onClick={()=>setForm(f=>({...f,type:t}))}
                      style={{flex:1,padding:'8px',border:'2px solid',borderRadius:'8px',cursor:'pointer',fontSize:'12px',fontWeight:'700',
                        borderColor:form.type===t?(t==='income'?'#16a34a':'#dc2626'):'#e2e8f0',
                        background:form.type===t?(t==='income'?'#f0fdf4':'#fef2f2'):'white',
                        color:form.type===t?(t==='income'?'#16a34a':'#dc2626'):'#94a3b8'}}>
                      {t==='income'?'▲ INCOME':'▼ EXPENSE'}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label style={{fontSize:'12px',fontWeight:'600',color:'#374151',display:'block',marginBottom:'4px'}}>Amount (₹)</label>
                <input type="number" value={form.amount} onChange={e=>setForm(f=>({...f,amount:e.target.value}))}
                  placeholder="0.00" style={{width:'100%',padding:'8px 12px',border:'1px solid #e2e8f0',borderRadius:'8px',fontSize:'13px',boxSizing:'border-box'}}/>
              </div>
              <div>
                <label style={{fontSize:'12px',fontWeight:'600',color:'#374151',display:'block',marginBottom:'4px'}}>Payment Mode</label>
                <select value={form.mode} onChange={e=>setForm(f=>({...f,mode:e.target.value}))}
                  style={{width:'100%',padding:'8px 12px',border:'1px solid #e2e8f0',borderRadius:'8px',fontSize:'13px'}}>
                  {MODES.map(m=><option key={m}>{m}</option>)}
                </select>
              </div>
              {form.category==='collection' && (
                <div style={{display:'grid',gap:'4px'}}>
                  <label style={{fontSize:'12px',fontWeight:'600',color:'#374151',display:'block',marginBottom:'4px'}}>Collection Type *</label>
                  <select value={collectionSubType} onChange={e=>{
                    setCollectionSubType(e.target.value);
                    if(['ont_sale','stb_sale','product_sale'].includes(e.target.value)){
                      setForm(f=>({...f,category:'product_sale',type:'income'}));
                    }
                  }} style={{width:'100%',padding:'8px 12px',border:'1px solid #e2e8f0',borderRadius:'8px',fontSize:'13px'}}>
                    <option value="broadband">📡 Broadband Renewal</option>
                    <option value="digitaltv">📺 Digital TV Renewal</option>
                    <option value="ont_sale">📦 ONT Sale</option>
                    <option value="stb_sale">📦 STB Sale</option>
                    <option value="product_sale">📦 Other Product Sale</option>
                    <option value="other">📈 Other Income</option>
                  </select>
                </div>
              )}

              {isProductCategory && (
                <div style={{border:'1px solid #e2e8f0',borderRadius:'10px',overflow:'hidden'}}>
                  {/* Section Header */}
                  <div style={{background:form.category==='product_purchase'?'#1e40af':'#16a34a',padding:'10px 14px',display:'flex',alignItems:'center',gap:'8px'}}>
                    <span style={{fontSize:'16px'}}>{form.category==='product_purchase'?'📦':'💰'}</span>
                    <span style={{color:'white',fontWeight:'700',fontSize:'13px'}}>{form.category==='product_purchase'?'Purchase Details':'Sale Details'}</span>
                  </div>
                  <div style={{padding:'12px',display:'grid',gap:'10px',background:'#f8fafc'}}>
                    {/* Product */}
                    <div style={{display:'grid',gridTemplateColumns:'1fr',gap:'4px'}}>
                      <label style={{fontSize:'11px',fontWeight:'700',color:'#64748b',textTransform:'uppercase',letterSpacing:'0.5px'}}>Product *</label>
                      <select value={invProduct} onChange={e=>{setInvProduct(e.target.value);if(e.target.value!=='__new__')setInvNewProduct('');}}
                        style={{padding:'8px 12px',border:'1px solid #e2e8f0',borderRadius:'8px',fontSize:'13px',background:'white',fontWeight:'600'}}>
                        <option value="">— Select Product —</option>
                        {products.map((p,i)=><option key={i} value={p.name}>{p.name}</option>)}
                        <option value="__new__">✚ Add New Product</option>
                      </select>
                    </div>
                    {(invProduct==='__new__'||!invProduct) && (
                      <div style={{display:'grid',gap:'4px'}}>
                        <label style={{fontSize:'11px',fontWeight:'700',color:'#64748b',textTransform:'uppercase',letterSpacing:'0.5px'}}>New Product Name *</label>
                        <input value={invNewProduct} onChange={e=>setInvNewProduct(e.target.value)}
                          placeholder="e.g. ONT, STB, Router, Cable Modem..."
                          style={{padding:'8px 12px',border:'1px solid #e2e8f0',borderRadius:'8px',fontSize:'13px',background:'white',boxSizing:'border-box',width:'100%'}}/>
                      </div>
                    )}
                    {/* Vendor + Model */}
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'8px'}}>
                      <div style={{display:'grid',gap:'4px'}}>
                        <label style={{fontSize:'11px',fontWeight:'700',color:'#64748b',textTransform:'uppercase',letterSpacing:'0.5px'}}>Vendor</label>
                        <input value={invVendor} onChange={e=>setInvVendor(e.target.value)}
                          placeholder="e.g. Huawei, TP-Link"
                          style={{padding:'8px 12px',border:'1px solid #e2e8f0',borderRadius:'8px',fontSize:'13px',background:'white'}}/>
                      </div>
                      <div style={{display:'grid',gap:'4px'}}>
                        <label style={{fontSize:'11px',fontWeight:'700',color:'#64748b',textTransform:'uppercase',letterSpacing:'0.5px'}}>Model</label>
                        <input value={invModel} onChange={e=>setInvModel(e.target.value)}
                          placeholder="e.g. EG8145V5"
                          style={{padding:'8px 12px',border:'1px solid #e2e8f0',borderRadius:'8px',fontSize:'13px',background:'white'}}/>
                      </div>
                    </div>
                    {/* Qty + Unit Price + Total */}
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'8px'}}>
                      <div style={{display:'grid',gap:'4px'}}>
                        <label style={{fontSize:'11px',fontWeight:'700',color:'#64748b',textTransform:'uppercase',letterSpacing:'0.5px'}}>Qty *</label>
                        <input type="number" value={invQty} min="1"
                          onChange={e=>handleInvQtyPrice(e.target.value,invUnitPrice)}
                          style={{padding:'8px 12px',border:'1px solid #e2e8f0',borderRadius:'8px',fontSize:'13px',background:'white',textAlign:'center',fontWeight:'700'}}/>
                      </div>
                      <div style={{display:'grid',gap:'4px'}}>
                        <label style={{fontSize:'11px',fontWeight:'700',color:'#64748b',textTransform:'uppercase',letterSpacing:'0.5px'}}>Unit Price ₹ *</label>
                        <input type="number" value={invUnitPrice}
                          onChange={e=>handleInvQtyPrice(invQty,e.target.value)}
                          placeholder="0.00"
                          style={{padding:'8px 12px',border:'1px solid #e2e8f0',borderRadius:'8px',fontSize:'13px',background:'white'}}/>
                      </div>
                      <div style={{display:'grid',gap:'4px'}}>
                        <label style={{fontSize:'11px',fontWeight:'700',color:'#64748b',textTransform:'uppercase',letterSpacing:'0.5px'}}>Total ₹</label>
                        <div style={{padding:'8px 12px',border:'2px solid',borderColor:form.category==='product_purchase'?'#1e40af':'#16a34a',borderRadius:'8px',fontSize:'13px',fontWeight:'800',color:form.category==='product_purchase'?'#1e40af':'#16a34a',background:'white',textAlign:'center'}}>
                          ₹{(parseFloat(form.amount)||0).toLocaleString('en-IN')}
                        </div>
                      </div>
                    </div>
                    {/* Purchase From / Customer */}
                    {form.category==='product_purchase' && (
                      <div style={{display:'grid',gap:'4px'}}>
                        <label style={{fontSize:'11px',fontWeight:'700',color:'#64748b',textTransform:'uppercase',letterSpacing:'0.5px'}}>Purchased From</label>
                        <input value={invPurchasedFrom} onChange={e=>setInvPurchasedFrom(e.target.value)}
                          placeholder="Distributor / Supplier name"
                          style={{padding:'8px 12px',border:'1px solid #e2e8f0',borderRadius:'8px',fontSize:'13px',background:'white',boxSizing:'border-box',width:'100%'}}/>
                      </div>
                    )}
                    {form.category==='product_sale' && (
                      <div style={{display:'grid',gap:'4px'}}>
                        <label style={{fontSize:'11px',fontWeight:'700',color:'#64748b',textTransform:'uppercase',letterSpacing:'0.5px'}}>Customer Username</label>
                        <input value={invCustomer} onChange={e=>setInvCustomer(e.target.value)}
                          placeholder="Link to BMS customer (optional)"
                          style={{padding:'8px 12px',border:'1px solid #e2e8f0',borderRadius:'8px',fontSize:'13px',background:'white',boxSizing:'border-box',width:'100%'}}/>
                      </div>
                    )}
                  </div>
                </div>
              )}
              {form.category==='bank_deposit' && (
                <div style={{display:'grid',gap:'4px'}}>
                  <label style={{fontSize:'12px',fontWeight:'600',color:'#374151',display:'block',marginBottom:'4px'}}>🏦 Bank Name *</label>
                  <input value={bankName} onChange={e=>setBankName(e.target.value)}
                    list="bank-list" placeholder="e.g. SBI, Federal Bank, Canara Bank..."
                    style={{width:'100%',padding:'8px 12px',border:'1px solid #e2e8f0',borderRadius:'8px',fontSize:'13px',boxSizing:'border-box'}}/>
                  <datalist id="bank-list">
                    {sources.filter(s=>s).map((s,i)=><option key={i} value={s}/>)}
                  </datalist>
                </div>
              )}

              {['chitty','loan'].includes(form.category) && (
                <div>
                  <label style={{fontSize:'12px',fontWeight:'600',color:'#374151',display:'block',marginBottom:'4px'}}>
                    {form.category==='loan'?'🏦 Bank / Lender Name':'📋 Chitty Name'} *
                  </label>
                  <input type="text" value={form.source} onChange={e=>setForm(f=>({...f,source:e.target.value}))}
                    list="sources-list" placeholder={form.category==='loan'?'e.g. SBI Home Loan, Federal Bank EMI':'e.g. KSFE Chitty, Club Chitty'}
                    style={{width:'100%',padding:'8px 12px',border:'1px solid #e2e8f0',borderRadius:'8px',fontSize:'13px',boxSizing:'border-box'}}/>
                  <datalist id="sources-list">
                    {sources.map((s,i)=><option key={i} value={s}/>)}
                  </datalist>
                </div>
              )}
              <div>
                <label style={{fontSize:'12px',fontWeight:'600',color:'#374151',display:'block',marginBottom:'4px'}}>Note</label>
                <input type="text" value={form.note} onChange={e=>setForm(f=>({...f,note:e.target.value}))}
                  placeholder="Optional description..."
                  style={{width:'100%',padding:'8px 12px',border:'1px solid #e2e8f0',borderRadius:'8px',fontSize:'13px',boxSizing:'border-box'}}/>
              </div>
              <div style={{display:'flex',gap:'8px',marginTop:'4px'}}>
                <button onClick={()=>{setShowForm(false);setEditEntry(null);}}
                  style={{flex:1,padding:'10px',border:'1px solid #e2e8f0',borderRadius:'8px',cursor:'pointer',fontSize:'13px',fontWeight:'600',background:'white'}}>
                  Cancel
                </button>
                <button onClick={isProductCategory?submitWithInventory:submit}
                  style={{flex:1,padding:'10px',border:'none',borderRadius:'8px',cursor:'pointer',fontSize:'13px',fontWeight:'700',
                    background:form.type==='income'?'#16a34a':'#dc2626',color:'white'}}>
                  {editEntry?'💾 Update':'✅ Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
